const RELATED_SELECTOR = '.tf-seo-links';
const COLLECTION_SELECTOR = "#MainContent[data-template^='collection']";
const REFERENCE_PATHS = new Set(['/collections/product-collection']);
const SUPPORTING_PAGE_PATHS = new Set([
  '/pages/about-tinyfigs',
  '/pages/how-to-order',
  '/pages/faq',
  '/pages/contact',
  '/policies/refund-policy',
  '/policies/shipping-policy',
]);
const GIFT_GUIDE_PATHS = new Set([
  '/pages/couples-gift',
  '/collections/anniversary-custom-minifigure-gifts',
  '/pages/wedding-cake-topper',
  '/collections/birthday-custom-minifigure-gifts',
  '/collections/graduation-gifts',
  '/collections/gifts-under-50',
  '/collections/long-distance-gifts',
  '/pages/gifts-for-couples',
  '/collections/desk-decorations',
  '/collections/custom-minifigure',
  '/pages/gifts-for-boyfriend',
  '/pages/gifts-for-girlfriend',
  '/pages/gifts-for-husband',
  '/pages/gifts-for-wife',
  '/pages/personalized-minifigure-frame-gifts',
  '/pages/gift-guide',
  '/pages/personalized-gifts',
  '/pages/gifts-for-him',
  '/pages/gifts-for-her',
  '/pages/gifts-for-kids',
  '/pages/gifts-under-50',
  '/pages/mothers-day-gifts',
  '/pages/fathers-day-gifts',
  '/pages/valentines-day-gifts',
  '/pages/graduation-gifts',
  '/pages/christmas-gifts',
  '/pages/unique-gifts',
  '/pages/last-minute-gifts',
  '/pages/gifts-for-grandparents',
  '/pages/wedding-custom-minifigure-gifts',
  '/pages/anniversary-custom-minifigure-gifts',
  '/pages/birthday-custom-minifigure-gifts',
  '/pages/team-custom-minifigure-gifts',
]);
const RELATED_GIFT_LINK_REPLACEMENTS = new Map([
  [
    '/collections/long-distance-gifts',
    { href: '/pages/gifts-for-couples', label: 'Gifts for couples' },
  ],
  [
    '/collections/desk-decorations',
    {
      href: '/pages/personalized-minifigure-frame-gifts',
      label: 'Display-ready frame gifts',
    },
  ],
]);

function setText(element, copy) {
  if (element && element.textContent?.trim() !== copy) element.textContent = copy;
}

export function normalizeRelatedGifts(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return 0;

  const sections = Array.from(root?.querySelectorAll?.(RELATED_SELECTOR) || []);

  sections.forEach((section) => {
    section.classList.add('tf-seo-links--enhanced', 'tfr-content-window');
    section.setAttribute('data-tfr-sectionized', 'related-gifts');
  });

  return sections.length;
}

export function normalizeCollectionIntro(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return false;

  const main = root?.querySelector?.(COLLECTION_SELECTOR);
  const path = normalizePathname(pathname);
  const authoredIntro = main?.querySelector?.('.tf-collection-heading');
  if(authoredIntro){
    authoredIntro.classList.add('tfr-collection-intro');
    markIntroPanel(authoredIntro);
    authoredIntro.closest('.shopify-section')?.classList.add('tfr-collection-intro-section');
    return true;
  }
  if (path === '/collections/custom-minifigure') {
    main?.classList.add('tfr-custom-minifigure-collection');
    let panel = main?.querySelector?.('.tfr-custom-collection-intro');
    if (panel) {
      markIntroPanel(panel);
      panel.classList.add('tfr-collection-intro');
      return true;
    }

    const chooser = main?.querySelector?.('.bm-all-products-intro');
    const heading = chooser?.querySelector?.('h1.bm-all-products-intro__title');
    const lead = chooser?.querySelector?.('.bm-all-products-intro__lede');
    if (!chooser || !heading || !lead) return false;

    panel = createIntroPanel(chooser, chooser.parentElement);
    if (!panel) return false;
    panel.classList.add('tfr-custom-collection-intro', 'tfr-collection-intro');
    panel.append(heading, lead);
    markIntroPanel(panel);
    return true;
  }

  const heading = main?.querySelector?.(
    "[id$='__section'] h1, .bm-all-products-intro h1, .collection-banner h1",
  );
  if (!heading) return false;

  const intro =
    heading?.closest?.('[data-testid="section-content"]') ||
    heading?.closest?.('.bm-all-products-intro');
  const copy = intro?.querySelector?.('rte-formatter.rte');
  const existingPanel = heading.closest?.('.tfr-intro-panel');

  let panel = intro && copy
    ? intro
    : existingPanel || createIntroPanel(heading, heading.parentElement);
  if (!panel) return false;

  if (!existingPanel && panel !== intro) panel.append(heading);

  panel.classList.add('tfr-collection-intro', 'tfr-intro-panel');
  panel.setAttribute('data-tfr-sectionized', 'intro');
  const introSection = panel.closest('.shopify-section');
  introSection?.classList.add('tfr-collection-intro-section');
  copy?.classList.add('tfr-collection-intro__copy');
  if (path === '/collections/product-collection' && copy) {
    setText(copy, 'Choose your gift format, personalize the details, and review your preview before shipping.');
  }
  return true;
}

export function removeNonCustomerMetadata(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return false;

  const main = findMain(root);
  if (!main) return false;
  const heading = main?.querySelector?.('h1');
  const intro =
    heading?.closest?.('[data-testid="section-content"]') ||
    heading?.closest?.('.section-content-wrapper');
  const metadata = Array.from(
    main.querySelectorAll?.(
      '.gift-guide-index__authority-row, .niche-gift__authority',
    ) || [],
  );
  const reviewedCopy = Array.from(main?.querySelectorAll?.('p, em') || []).find((element) =>
    element.textContent?.includes('Reviewed by TinyFigs Personalization Team'),
  );

  if (normalizePathname(pathname) === '/pages/faq') {
    intro?.classList.add('tfr-faq-intro');
  }
  metadata.forEach((metadata) => metadata.remove());
  reviewedCopy?.remove();
  return Boolean(intro || metadata.length || reviewedCopy);
}

export function normalizeRelatedGiftLinks(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return 0;

  const links = Array.from(
    root?.querySelectorAll?.(`${RELATED_SELECTOR} a[href]`) || [],
  );
  let normalized = 0;
  links.forEach((link) => {
    const rawHref = link.getAttribute('href');
    if (!rawHref) return;

    let linkPath = rawHref;
    try {
      linkPath = new URL(rawHref, 'https://tinyfigs.com').pathname;
    } catch (_error) {
      linkPath = rawHref;
    }
    const replacement = RELATED_GIFT_LINK_REPLACEMENTS.get(
      normalizePathname(linkPath),
    );
    if (!replacement) return;

    link.setAttribute('href', replacement.href);
    setText(link, replacement.label);
    normalized += 1;
  });
  return normalized;
}

export function relocateCollectionRelatedGifts(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return false;

  const main = root?.querySelector?.(COLLECTION_SELECTOR);
  const related = main?.querySelector?.(RELATED_SELECTOR);
  const productGrid = main?.querySelector?.('.main-collection-grid');
  const resultsSection = productGrid?.closest('.shopify-section');

  if (!main || !related || !resultsSection) return false;

  let shell = main.querySelector('.tfr-related-gifts-shell');
  if (!shell) {
    shell = document.createElement('div');
    shell.className = 'tfr-related-gifts-shell page-width';
    resultsSection.insertAdjacentElement('afterend', shell);
  }

  if (related.parentElement !== shell) shell.append(related);
  return true;
}

export function normalizePathname(pathname = '/') {
  const cleanPath = String(pathname || '/')
    .split(/[?#]/, 1)[0]
    .replace(/\/{2,}/g, '/')
    .replace(/\/+$/, '');

  return cleanPath || '/';
}

function isAllowedPath(pathname) {
  const path = normalizePathname(pathname);
  return (
    REFERENCE_PATHS.has(path) ||
    SUPPORTING_PAGE_PATHS.has(path) ||
    GIFT_GUIDE_PATHS.has(path)
  );
}

export function normalizePolicyHeading(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  const path = normalizePathname(pathname);
  if (!isAllowedPath(path) || !path.startsWith('/policies/')) return false;

  const pageHeading = root?.querySelector?.('#MainContent .shopify-policy__title h1');
  const bodyHeading = root?.querySelector?.('#MainContent .shopify-policy__body h1');
  if (!pageHeading || !bodyHeading) return false;

  setText(pageHeading, bodyHeading.textContent.trim());
  bodyHeading.remove();
  return true;
}

function findMain(root) {
  if (root?.matches?.('#MainContent')) return root;
  return root?.querySelector?.('#MainContent') || null;
}

function markIntroPanel(panel) {
  if (!panel) return null;

  panel.classList.add('tfr-intro-panel');
  panel.setAttribute('data-tfr-sectionized', 'intro');
  syncIntroPanelVariant(panel);
  return panel;
}

function syncIntroPanelVariant(panel) {
  if (!panel) return null;

  const hasExplanatoryCopy = Boolean(
    panel.querySelector?.('p, li, .tf-guide-intro, rte-formatter, .rte'),
  );
  if (hasExplanatoryCopy) panel.classList.remove('tfr-intro-panel--title-only');
  else panel.classList.add('tfr-intro-panel--title-only');
  return panel;
}

function markContentWindow(window) {
  if (!window) return null;

  window.classList.add('tfr-content-window');
  window.setAttribute('data-tfr-sectionized', 'content-window');
  return window;
}

function ownerDocument(element, fallback) {
  return element?.ownerDocument || fallback?.ownerDocument || fallback ||
    (typeof document === 'undefined' ? null : document);
}

function directChildren(element) {
  return Array.from(element?.children || []);
}

function directHeadings(element) {
  return directChildren(element).filter((child) => child.tagName === 'H2');
}

function isExcludedSectionNode(node) {
  return (
    node?.nodeType === 1 &&
    node.matches?.(
      '.tf-seo-links, .tfr-intro-panel, .tfr-content-window, script, style',
    )
  );
}

function findRichTextHosts(scope) {
  const selectors = [
    '.tf-guide-page',
    '.tinyfigs-direct-page__body',
    '.niche-gift__article',
    '.shopify-policy__body',
    '.section-content-wrapper',
    '[data-testid="section-content"]',
    'rte-formatter',
    'rte-formatter.rte',
    '.rte',
  ].join(',');
  const candidates = Array.from(scope?.querySelectorAll?.(selectors) || []).filter(
    (host) => !host.closest?.('.tf-seo-links, .tfr-intro-panel'),
  );

  candidates.sort((left, right) => {
    let leftDepth = 0;
    let rightDepth = 0;
    for (let node = left; node; node = node.parentElement) leftDepth += 1;
    for (let node = right; node; node = node.parentElement) rightDepth += 1;
    return rightDepth - leftDepth;
  });

  return candidates.filter((host, index, hosts) => {
    const nestedHost = hosts.some(
      (candidate, candidateIndex) =>
        candidateIndex < index && host.contains(candidate),
    );
    return !nestedHost || directHeadings(host).length > 0;
  });
}

function findLeadingParagraph(host) {
  if (!host) return null;

  const guideLead = host.querySelector?.(
    ':scope > .tf-guide-page > .tf-guide-intro, :scope > .tf-guide-intro',
  );
  if (guideLead && !guideLead.closest?.('.tf-seo-links')) return guideLead;

  const children = directChildren(host);
  const firstHeadingIndex = children.findIndex((child) => child.tagName === 'H2');
  const beforeHeading =
    firstHeadingIndex === -1 ? children : children.slice(0, firstHeadingIndex);
  const paragraph = beforeHeading.find(
    (child) => child.tagName === 'P' && !child.closest?.('.tf-seo-links'),
  );
  if (paragraph) return paragraph;

  for (const child of beforeHeading) {
    const nestedParagraph = findLeadingParagraph(child);
    if (nestedParagraph) return nestedParagraph;
  }

  const nestedHost = findRichTextHosts(host).find((candidate) => candidate !== host);
  return nestedHost ? findLeadingParagraph(nestedHost) : null;
}

function findMainHeading(main) {
  const selectors = [
    '.tinyfigs-direct-page__header h1',
    '.niche-gift__header h1',
    '.shopify-policy__title h1',
    '.tfr-faq-intro h1',
    'h1',
  ];

  for (const selector of selectors) {
    const heading = main?.querySelector?.(selector);
    if (heading && !heading.closest?.('script, style')) return heading;
  }
  return null;
}

function createIntroPanel(before, fallbackParent) {
  const documentRef = ownerDocument(before, fallbackParent);
  if (!documentRef?.createElement) return null;

  const panel = documentRef.createElement('section');
  panel.className = 'tfr-intro-panel';
  panel.setAttribute('data-tfr-sectionized', 'intro');
  const parent = before?.parentElement || fallbackParent;
  if (!parent) return null;
  parent.insertBefore(panel, before || null);
  return panel;
}

function findDirectNicheBody(article) {
  return directChildren(article).find(
    (child) =>
      child.matches?.('.niche-gift__body') &&
      !child.matches?.('.niche-gift__body--mobile-hero'),
  ) || null;
}

function ensureNicheGiftIntro(main) {
  const article = main?.querySelector?.('.niche-gift__article');
  if (!article) return null;

  const header = directChildren(article).find((child) =>
    child.matches?.('.niche-gift__header'),
  );
  const body = findDirectNicheBody(article);
  let panel = directChildren(article).find((child) =>
    child.matches?.('.tfr-intro-panel'),
  );
  if (!panel) panel = createIntroPanel(header || body, article);
  if (!panel) return null;

  markIntroPanel(panel);
  const heading =
    panel.querySelector?.(':scope > h1') ||
    header?.querySelector?.(':scope > h1') ||
    article.querySelector?.('.niche-gift__title');
  if (heading && heading.parentElement !== panel) panel.append(heading);

  const lead =
    panel.querySelector?.('p') ||
    header?.querySelector?.('.tf-guide-lede') ||
    panel.querySelector?.(':scope > .tf-guide-mobile-hero__intro') ||
    article.querySelector?.('.tf-guide-mobile-hero__intro') ||
    findLeadingParagraph(body);
  if (lead && lead.parentElement !== panel) panel.append(lead);

  syncIntroPanelVariant(panel);
  article.classList.add('tfr-content-stack');
  body?.classList.add('tfr-content-stack__body');
  if (header && directChildren(header).length === 0) header.remove();
  return panel;
}

function ensureDirectPageIntro(main) {
  const article = main?.querySelector?.(
    '.tinyfigs-direct-page__article, .niche-gift__article',
  );
  if (!article) return null;

  const header = article.querySelector?.(
    ':scope > .tinyfigs-direct-page__header, :scope > .niche-gift__header',
  );
  const body = article.querySelector?.(
    ':scope > .tinyfigs-direct-page__body, :scope > .niche-gift__body',
  );
  let panel = article.querySelector?.(':scope > .tfr-intro-panel');
  if (!panel) panel = createIntroPanel(header || body, article);
  if (!panel) return null;

  markIntroPanel(panel);
  if (header && header.parentElement !== panel) panel.append(header);

  const lead = panel.querySelector?.('p') ? null : findLeadingParagraph(body);
  if (lead && lead.parentElement !== panel) panel.append(lead);

  syncIntroPanelVariant(panel);

  article.classList.add('tfr-content-stack');
  body?.classList.add('tfr-content-stack__body');
  return panel;
}

function ensurePolicyIntro(main) {
  const title = main?.querySelector?.('.shopify-policy__title');
  const body = main?.querySelector?.('.shopify-policy__body');
  if (!title || !body) return null;

  const container = title.closest?.('.shopify-policy__container') || title.parentElement;
  let panel = container?.querySelector?.(':scope > .tfr-intro-panel');
  if (!panel) panel = createIntroPanel(title, container);
  if (!panel) return null;

  markIntroPanel(panel);
  if (title.parentElement !== panel) panel.append(title);

  const lead = panel.querySelector?.('p') ? null : findLeadingParagraph(body);
  if (lead && lead.parentElement !== panel) panel.append(lead);

  syncIntroPanelVariant(panel);

  body.classList.add('tfr-content-stack');
  return panel;
}

function findCollectionIntro(main) {
  return main?.querySelector?.(
    '.tfr-intro-panel, .tfr-collection-intro, .bm-all-products-intro',
  ) || null;
}

function ensureGenericIntro(main) {
  const existingPanel = main?.querySelector?.('.tfr-intro-panel');
  if (existingPanel) return markIntroPanel(existingPanel);

  const existingFaqIntro = main?.querySelector?.('.tfr-faq-intro');
  if (existingFaqIntro) return markIntroPanel(existingFaqIntro);

  const heading = findMainHeading(main);
  const headingPanel = heading?.closest?.('.tfr-intro-panel');
  if (headingPanel) return markIntroPanel(headingPanel);

  const host = findRichTextHosts(main)[0];
  if (!heading || heading.closest?.('.tf-seo-links')) return null;

  const container =
    heading.closest?.('[data-testid="section-content"]') || heading.parentElement;
  const lead = host ? findLeadingParagraph(host) : null;
  if (!container) return null;

  const panel = createIntroPanel(heading, container.parentElement || container);
  if (!panel) return null;
  if (heading.parentElement !== panel) panel.append(heading);
  if (lead && lead.parentElement !== panel) panel.append(lead);
  return syncIntroPanelVariant(panel);
}

export function adaptGiftGuideSurface(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  const path = normalizePathname(pathname);
  const main = findMain(root);
  if (!main || !GIFT_GUIDE_PATHS.has(path)) return 0;

  let adapted = 0;
  const index = main.querySelector?.('.gift-guide-index');
  if (index) {
    index.classList.add('tfr-content-stack');
    const hero = index.querySelector?.('.gift-guide-index__hero');
    if (hero) {
      markIntroPanel(hero);
      adapted += 1;
    }
    Array.from(index.querySelectorAll?.('.gift-guide-index__section') || []).forEach(
      (section) => {
        markContentWindow(section);
        adapted += 1;
      },
    );
  }

  const nicheArticle = main.querySelector?.('.niche-gift__article');
  if (nicheArticle) {
    nicheArticle.classList.add('tfr-content-stack');
    const header = directChildren(nicheArticle).find((child) =>
      child.matches?.('.niche-gift__header'),
    );
    const body = findDirectNicheBody(nicheArticle);
    const mobileBody =
      directChildren(nicheArticle).find((child) =>
        child.matches?.('.niche-gift__body--mobile-hero'),
      ) ||
      directChildren(header).find((child) =>
        child.matches?.('.niche-gift__body--mobile-hero'),
      );
    const signalGrid =
      directChildren(nicheArticle).find((child) =>
        child.matches?.('.niche-gift__signal-grid'),
      ) ||
      directChildren(header).find((child) =>
        child.matches?.('.niche-gift__signal-grid'),
      );

    [mobileBody, signalGrid].filter(Boolean).forEach((context) => {
      if (context.parentElement === header) {
        nicheArticle.insertBefore(context, body || null);
      }
      markContentWindow(context);
      adapted += 1;
    });

    directChildren(nicheArticle)
      .filter((child) =>
        child.matches?.('.niche-gift__products, .niche-gift__related'),
      )
      .forEach((context) => {
        markContentWindow(context);
        adapted += 1;
      });
  }

  return adapted;
}

export function ensureIntroPanel(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  const main = findMain(root);
  if (!main) return null;

  const path = normalizePathname(pathname);
  if (!isAllowedPath(path)) return null;
  if (path.startsWith('/collections/')) {
    return markIntroPanel(findCollectionIntro(main));
  }

  if (path.startsWith('/policies/')) return ensurePolicyIntro(main);
  if (main.querySelector?.('.niche-gift__article')) return ensureNicheGiftIntro(main);
  return ensureDirectPageIntro(main) || ensureGenericIntro(main);
}

function sectionizeNicheBody(body) {
  if (!body || body.closest?.('.tfr-intro-panel, .tfr-content-window')) return 0;

  const children = directChildren(body);
  const firstHeadingIndex = children.findIndex((child) => child.tagName === 'H2');
  const leadingChildren = (
    firstHeadingIndex === -1 ? children : children.slice(0, firstHeadingIndex)
  ).filter((child) => !isExcludedSectionNode(child));
  let windows = 0;

  if (leadingChildren.length) {
    const documentRef = ownerDocument(body);
    const overview = documentRef?.createElement?.('section');
    if (overview) {
      markContentWindow(overview);
      body.insertBefore(overview, leadingChildren[0]);
      leadingChildren.forEach((child) => overview.append(child));
      windows += 1;
    }
  }

  return windows + sectionizeHost(body);
}

function sectionizeHost(host) {
  if (!host || host.closest?.('.tfr-intro-panel, .tfr-content-window')) return 0;

  let headings = directHeadings(host);
  if (!headings.length) {
    const nestedHost = directChildren(host).find(
      (child) => !isExcludedSectionNode(child) && directHeadings(child).length > 0,
    );
    if (nestedHost) return sectionizeHost(nestedHost);
  }

  let windows = 0;

  headings.forEach((heading, index) => {
    if (heading.closest?.('.tfr-content-window')) return;

    const nextHeading = headings[index + 1] || null;
    const documentRef = ownerDocument(heading, host);
    const window = documentRef?.createElement?.('section');
    if (!window) return;

    window.className = 'tfr-content-window';
    window.setAttribute('data-tfr-sectionized', 'content-window');
    host.insertBefore(window, heading);

    let node = heading;
    while (node && node !== nextHeading && !isExcludedSectionNode(node)) {
      const nextNode = node.nextSibling;
      window.append(node);
      node = nextNode;
    }

    windows += 1;
  });

  directChildren(host).forEach((child) => {
    if (isExcludedSectionNode(child)) return;
    if (
      child.matches?.(
        'section:not(.tf-seo-links), .tf-order-steps, .tf-order-actions, .tf-order-details, accordion-custom',
      )
    ) {
      if (!child.closest?.('.tfr-content-window')) {
        markContentWindow(child);
        windows += 1;
      }
    }
  });

  return windows;
}

export function sectionizeSupportingSections(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return 0;

  const main = findMain(root) || root;
  const sections = directChildren(main).filter((section) => {
    if (!section.matches?.('.shopify-section')) return false;
    if (section.matches?.('.tfr-intro-panel, .tfr-content-window')) return false;
    if (section.querySelector?.('.tfr-intro-panel')) return false;
    return Boolean(section.querySelector?.('form') || section.querySelector?.('.contact-form'));
  });

  sections.forEach(markContentWindow);
  return sections.length;
}

export function sectionizeRichText(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  if (!isAllowedPath(pathname)) return 0;

  const main = findMain(root) || root;
  const hosts = findRichTextHosts(main);
  return hosts.reduce((count, host) => count + sectionizeHost(host), 0);
}

export function classifySupportingPage(
  root = document,
  pathname = typeof window === 'undefined' ? '/' : window.location.pathname,
) {
  const path = normalizePathname(pathname);
  const main = findMain(root);
  const isReference = REFERENCE_PATHS.has(path);
  const isSupport = SUPPORTING_PAGE_PATHS.has(path);
  const isGiftGuide = GIFT_GUIDE_PATHS.has(path);

  if (!main || !isAllowedPath(path)) return 0;
  if (path === '/pages/faq' && main.querySelector?.('.tf-faq')) return 0;
  // These guides are already sectioned in Liquid; do not move their heading/body.
  if (main.querySelector?.('[data-tfg-guide]')) return 0;

  main.classList.add('tfr-supporting-page');
  if (isReference) main.classList.add('tfr-supporting-page--reference');
  if (isSupport) main.classList.add('tfr-supporting-page--support');
  if (isGiftGuide) main.classList.add('tfr-supporting-page--gift-guide');

  adaptGiftGuideSurface(main, path);
  const intro = ensureIntroPanel(main, path);
  main.querySelectorAll?.('.tf-seo-links--enhanced').forEach(markContentWindow);
  if (!path.startsWith('/collections/')) {
    const nicheBody = main.querySelector?.(
      '.niche-gift__article > .niche-gift__body:not(.niche-gift__body--mobile-hero)',
    );
    if (nicheBody) sectionizeNicheBody(nicheBody);
    sectionizeRichText(main, path);
    sectionizeSupportingSections(main, path);
  }
  return intro ? 1 : 0;
}

function enhanceRelatedGifts() {
  const pathname = normalizePathname(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );
  normalizeRelatedGifts(document, pathname);
  normalizeRelatedGiftLinks(document, pathname);
  normalizeCollectionIntro(document, pathname);
  removeNonCustomerMetadata(document, pathname);
  relocateCollectionRelatedGifts(document, pathname);
  normalizePolicyHeading(document, pathname);
  classifySupportingPage(document, pathname);
}

let enhancementScheduled = false;
function scheduleEnhancement() {
  if (enhancementScheduled) return;
  enhancementScheduled = true;
  queueMicrotask(() => {
    enhancementScheduled = false;
    enhanceRelatedGifts();
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceRelatedGifts, { once: true });
  } else {
    enhanceRelatedGifts();
  }

  if (typeof MutationObserver === 'function') {
    new MutationObserver(scheduleEnhancement).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
}
